/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";

import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import { useAuth } from "react-oidc-context";
import EditIcon from "@mui/icons-material/Edit";
import CustomTooltip from "../../../code-connect/component/CustomTooltip";
import CustomerFilterArrowIcon from "../../../svg/selectRailStationCustomerDownArrow.svg";
import NitroService from "../../../utils/nitro.service";
import ILocation, { ICarrier, IRailStation } from "../../../types/ILocation";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { useIsMobile } from "../../../hooks/useIsMobile";
import RailStationLookupPanel from "../../../panels/mobile/railStationLookupPanel/RailStationLookupPanel";
import RailStationCarrierReviewPanel from "../../../panels/mobile/railStationLookupPanel/RailStationCarrierReviewPanel";
import RailStationAddCarrierPanel from "../../../panels/mobile/railStationLookupPanel/RailStationAddCarrierPanel";
import { toModes } from "../../../utils/utils";
import { useNitroStore } from "../../../store/Store";
import TrashCanIcon from "../../../svg/trashCanIcon.svg";
import RailSettingsOLD from "./RailSettingsOLD";
import DeleteRailStationConfirmation from "./DeleteRailStationConfirmation";
import { IStationRow } from "../../../types/IRailFreightDetails";

export const MAIN_RAIL_STATION_ROW_ID = "main";

export const getRailStationRowOrder = (
  rowId: string,
  seededRowIds: string[],
  allRowIds: string[],
): number => {
  if (rowId === MAIN_RAIL_STATION_ROW_ID) {
    return 0;
  }

  const seededIndex = seededRowIds.indexOf(rowId);
  if (seededIndex >= 0) {
    return seededIndex + 1;
  }

  const addedIndex = allRowIds
    .filter((id) => !seededRowIds.includes(id))
    .indexOf(rowId);
  return seededRowIds.length + 1 + addedIndex;
};

const formatStationDisplay = (station: IRailStation) => {
  const carrierIds = station.carriers.map((carrier) => carrier.id);
  return carrierIds.length
    ? `${station.stationState} (${carrierIds.join(", ")})`
    : station.stationState;
};

// Returns the hover tooltip text for a station's manually added carriers, or null if none were added manually.
const getManuallyAddedCarriersTooltip = (
  station: IRailStation,
): string | null => {
  const manuallyAddedCarriers = station.carriers.filter(
    (carrier) => carrier.manuallyCreated,
  );
  if (!manuallyAddedCarriers.length) return null;

  const carrierIds = manuallyAddedCarriers
    .map((carrier) => carrier.id)
    .join(", ");

  return `Manually added carriers: ${carrierIds}. Added by ${
    manuallyAddedCarriers[0].createdBy || "Unknown"
  }.`;
};

const DisabledRailSettings = ({ location }: { location: ILocation }) => {
  const [featureFlags] = useNitroStore((state) => [state.featureFlags]);
  const { multiRailStation } = featureFlags;
  // TODO: remove old code
  const legacyStation: IRailStation[] = location.stationState
    ? [
        {
          stationState: location.stationState,
          scrsId: location.scrsId,
          carriers: location.carriers.map((c) => ({
            ...c,
            createdBy: "",
          })),
        },
      ]
    : [];
  const stations = location.railStations.length
    ? location.railStations
    : legacyStation;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        paddingRight: 3,
        paddingTop: 3,
        paddingBottom: 3,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 14, color: "#62748e", lineHeight: "14px" }}>
          Stations &amp; Carriers*
        </div>
        <div
          id="rail-station-name"
          style={{
            fontSize: 14,
            color: stations.length ? "#0a0a0a" : "#878787",
            lineHeight: "17.5px",
            padding: "4px 8px",
            borderRadius: 3.5,
            ...(multiRailStation && {
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 12,
              alignSelf: "stretch",
            }),
          }}
        >
          {stations.length ? (
            stations.map((station) => {
              const tooltip = getManuallyAddedCarriersTooltip(station);
              const rowKey = `${
                station.scrsId || station.stationState
              }-${station.carriers.map((carrier) => carrier.id).join(",")}`;

              return (
                <CustomTooltip
                  key={rowKey}
                  title={tooltip || ""}
                  variant="dark"
                  placement="bottom-start"
                  arrow
                >
                  <div data-testid="rail-station-display">
                    {formatStationDisplay(station)}
                  </div>
                </CustomTooltip>
              );
            })
          ) : (
            <span style={{ fontStyle: "italic" }}>No station selected</span>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 14, color: "#62748e", lineHeight: "14px" }}>
          Equipment types
        </div>
        <div
          style={{
            fontSize: 14,
            color:
              location.equipmentTypes && location.equipmentTypes.length > 0
                ? "#0a0a0a"
                : "#878787",
            lineHeight: "17.5px",
            padding: "4px 8px",
            borderRadius: 3.5,
          }}
        >
          {location.equipmentTypes && location.equipmentTypes.length > 0 ? (
            location.equipmentTypes.join(", ")
          ) : (
            <span style={{ fontStyle: "italic" }}>No equipment type</span>
          )}
        </div>
      </div>
    </div>
  );
};

const EquipmentDropdownIcon = ({
  style,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span {...rest} style={{ ...style, transform: "none" }}>
    <img
      src={CustomerFilterArrowIcon}
      alt=""
      style={{ width: 16, height: 16, pointerEvents: "none" }}
    />
  </span>
);

type RailSettingsProps = {
  originalLocation?: ILocation;
  location: ILocation;
  setLocation: (location: ILocation) => void;
  zipCode: string;
  disabled?: boolean;
  onAdditionalRowsDirtyChange?: (hasDirtyAdditionalRow: boolean) => void;
  onRailStationsChange?: (railStations: IStationRow[]) => void;
};

// TODO: (multiRailStation): once the flag is fully rolled out, drop this wrapper, delete
// RailSettingsOLD.tsx, and rename RailSettingsMultiStation below to RailSettings.
const RailSettings = (props: RailSettingsProps) => {
  const [featureFlags] = useNitroStore((state) => [state.featureFlags]);
  if (!featureFlags.multiRailStation) {
    return <RailSettingsOLD {...props} />;
  }
  return <RailSettingsMultiStation {...props} />;
};

const RailSettingsMultiStation = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  originalLocation: _originalLocation,
  location,
  setLocation,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  zipCode: _zipCode,
  disabled,
  onAdditionalRowsDirtyChange,
  onRailStationsChange,
}: RailSettingsProps) => {
  const auth = useAuth();
  const isMobile = useIsMobile();
  const [showRailStationLookupPanel, setShowRailStationLookupPanel] =
    useState(false);
  const [showRailCarrierReviewPanel, setShowRailCarrierReviewPanel] =
    useState(false);
  const [featureFlags, pendingRailStationUpdate, setPendingRailStationUpdate] =
    useNitroStore((state) => [
      state.featureFlags,
      state.pendingRailStationUpdate,
      state.setPendingRailStationUpdate,
    ]);
  const [showRailAddCarrierPanel, setShowRailAddCarrierPanel] = useState(false);
  const [additionalStationRowIds, setAdditionalStationRowIds] = useState<
    string[]
  >([]);
  const [additionalStationData, setAdditionalStationData] = useState<
    Record<string, IRailStation>
  >({});
  const [activeSearchRowId, setActiveSearchRowId] = useState<string | null>(
    null,
  );

  const [pendingDeleteRowId, setPendingDeleteRowId] = useState<string | null>(
    null,
  );
  const [selectedLookupStation, setSelectedLookupStation] = useState("");
  const [selectedLookupScrsId, setSelectedLookupScrsId] = useState("");
  const [lookupUserAddedCarriers, setLookupUserAddedCarriers] = useState<
    string[]
  >([]);
  type LookupCarrierEntry = {
    id: string;
    selected: boolean;
    manuallyCreated: boolean;
    createdBy: string;
  };
  const [lookupRailCarriers, setLookupRailCarriers] = useState<
    Record<string, LookupCarrierEntry>
  >({});
  const [loadingLookupRailCarriers, setLoadingLookupRailCarriers] =
    useState(false);
  const [setDestinationToTroubleshoot, setDisplayAddInboundRailPanel] =
    useNitroCAStore((state) => [
      state.setDestinationToTroubleshoot,
      state.setDisplayAddInboundRailPanel,
    ]);

  // Refs to keep track of the initial state of additional rail stations and the seeded location ID.
  // TODO: remove later possibly
  const seededLocationIdRef = useRef<string | null>(null);
  const initialAdditionalStationsSnapshotRef = useRef<string>("[]");
  useEffect(() => {
    if (seededLocationIdRef.current === location.id) {
      return;
    }
    seededLocationIdRef.current = location.id;

    const extraStations = location.railStations?.slice(1) || [];
    initialAdditionalStationsSnapshotRef.current = JSON.stringify(
      extraStations.map((station) => ({
        scrsId: station.scrsId || null,
        stationState: station.stationState,
        carriers: station.carriers.map((carrier) => carrier.id),
      })),
    );

    if (extraStations.length === 0) {
      setAdditionalStationRowIds([]);
      setAdditionalStationData({});
      return;
    }

    const rowIds = extraStations.map(
      (_, index) => `existing-${location.id}-${index}`,
    );
    const dataByRowId: typeof additionalStationData = {};
    extraStations.forEach((station, index) => {
      dataByRowId[rowIds[index]] = {
        scrsId: station.scrsId,
        stationState: station.stationState,
        carriers: station.carriers,
      };
    });
    setAdditionalStationRowIds(rowIds);
    setAdditionalStationData(dataByRowId);
  }, [location.id, location.railStations]);

  // Update additional station data when a pending rail station update is available
  useEffect(() => {
    if (
      !pendingRailStationUpdate ||
      pendingRailStationUpdate.locationId !== location.id ||
      !activeSearchRowId
    ) {
      return;
    }
    setAdditionalStationData((prev) => ({
      ...prev,
      [activeSearchRowId]: {
        scrsId: pendingRailStationUpdate.scrsId,
        stationState: pendingRailStationUpdate.stationState,
        carriers: pendingRailStationUpdate.carriers,
      },
    }));
    setAdditionalStationRowIds((prev) =>
      prev.includes(activeSearchRowId) ? prev : [...prev, activeSearchRowId],
    );
    setActiveSearchRowId(null);
    setPendingRailStationUpdate(undefined);
  }, [pendingRailStationUpdate]);

  // Track changes to additional station rows and notify if any row is dirty
  useEffect(() => {
    const currentAdditionalStations = additionalStationRowIds
      .map((rowId) => additionalStationData[rowId])
      .filter((station): station is NonNullable<typeof station> =>
        Boolean(station),
      )
      .map((station) => ({
        scrsId: station.scrsId,
        stationState: station.stationState,
        carriers: station.carriers.map((carrier) => carrier.id),
      }));
    const hasDirtyAdditionalRow =
      JSON.stringify(currentAdditionalStations) !==
      initialAdditionalStationsSnapshotRef.current;
    onAdditionalRowsDirtyChange?.(hasDirtyAdditionalRow);
  }, [additionalStationRowIds, additionalStationData]);

  // Update rail stations whenever the location or additional station data changes
  const lastSyncedRailStationsRef = useRef<string | null>(null);
  useEffect(() => {
    const combinedRailStations = [
      ...(location.stationState
        ? [
            {
              scrsId: location.scrsId,
              stationState: location.stationState,
              carriers: location.carriers,
            },
          ]
        : []),
      ...additionalStationRowIds.flatMap((rowId) => {
        const station = additionalStationData[rowId];
        return station
          ? [
              {
                scrsId: station.scrsId || "",
                stationState: station.stationState,
                carriers: station.carriers,
              },
            ]
          : [];
      }),
    ];
    const serialized = JSON.stringify(combinedRailStations);
    if (serialized !== lastSyncedRailStationsRef.current) {
      lastSyncedRailStationsRef.current = serialized;
      setLocation({ ...location, railStations: combinedRailStations });
    }

    // TODO: optional function
    onRailStationsChange?.(
      combinedRailStations.map((station) => ({
        scrsId: station.scrsId,
        stationState: station.stationState,
        carriers: station.carriers.map((carrier) => carrier.id),
      })),
    );
  }, [location, additionalStationRowIds, additionalStationData]);

  const stationRowCount =
    (location.stationState ? 1 : 0) +
    additionalStationRowIds.filter((rowId) => additionalStationData[rowId])
      .length;
  const prevStationRowCountRef = useRef(stationRowCount);

  useEffect(() => {
    const prevStationRowCount = prevStationRowCountRef.current;
    prevStationRowCountRef.current = stationRowCount;

    if (prevStationRowCount === 0 || stationRowCount > 0) {
      return;
    }

    const hasRailMode =
      location.inboundModes.includes("RAIL") ||
      location.outboundModes.includes("RAIL");
    if (!hasRailMode) {
      return;
    }

    setLocation({
      ...location,
      inboundModes: location.inboundModes.filter((mode) => mode !== "RAIL"),
      outboundModes: location.outboundModes.filter((mode) => mode !== "RAIL"),
      stationState: undefined,
      scrsId: undefined,
      carriers: [],
      equipmentTypes: [],
      railStations: [],
    });
  }, [stationRowCount]);

  if (disabled) {
    return <DisabledRailSettings location={location} />;
  }

  let stationDisplay = "";
  if (location.stationState) {
    const carrierList = location.carriers?.map((carrier) => carrier.id);
    stationDisplay = carrierList?.length
      ? `${location.stationState} (${carrierList.join(", ")})`
      : location.stationState;
  }

  const handleOpenSearch = (rowId: string | null = null) => {
    setActiveSearchRowId(rowId);

    if (isMobile) {
      setShowRailCarrierReviewPanel(false);
      setShowRailStationLookupPanel(true);
      return;
    }

    setDestinationToTroubleshoot({
      id: location.id,
      name: location.name,
      city: location.city,
      stateOrProvince: location.state,
      coordinate: location.coordinates as [number, number],
      isCity: false,
      displayName: location.name,
      isSurroundingLocation: false,
      availableProducts: [],
      details: {
        ...location,
      },
      targetRowId: rowId ?? undefined,
    });
    setDisplayAddInboundRailPanel(true);
  };

  const handleLookupRailStationNext = async (station: {
    name: string;
    value: string;
    scrsId?: string | null;
    carriers?: string[];
  }) => {
    const existingCarrierManualState = new Map(
      location.carriers.map((c) => [c.id, c]),
    );

    setSelectedLookupStation(station.value);
    setSelectedLookupScrsId(station.scrsId || "");
    setLookupUserAddedCarriers([]);
    setShowRailStationLookupPanel(false);
    setShowRailCarrierReviewPanel(true);
    setLoadingLookupRailCarriers(true);

    try {
      const stationCarriers = (station.carriers || [])
        .filter((id) => Boolean(id))
        .map((id) => ({
          id,
          manuallyCreated:
            existingCarrierManualState.get(id)?.manuallyCreated ?? false,
          createdBy: existingCarrierManualState.get(id)?.createdBy ?? "",
        }));

      if (stationCarriers.length > 0) {
        const carriersMap = stationCarriers.reduce<
          Record<string, LookupCarrierEntry>
        >((acc, carrier) => {
          if (!carrier?.id) return acc;
          acc[carrier.id] = {
            id: carrier.id,
            selected: true,
            manuallyCreated: carrier.manuallyCreated,
            createdBy: carrier.createdBy,
          };
          return acc;
        }, {});

        setLookupRailCarriers(carriersMap);
      } else {
        const records = await NitroService.getCarriersByRailStation({
          stationState: station.value,
        });

        const carriersMap: Record<string, LookupCarrierEntry> = {};
        records.forEach((record) => {
          if (!record?.id) return;
          carriersMap[record.id] = {
            id: record.id,
            selected: true,
            manuallyCreated: record.manuallyCreated,
            createdBy: record.createdBy,
          };
        });

        setLookupRailCarriers(carriersMap);
      }
    } catch (error) {
      console.error("Error loading carriers:", error);
      const fallbackCarriersMap: Record<string, LookupCarrierEntry> = {};
      (station.carriers || []).forEach((carrierId) => {
        if (!carrierId) return;
        fallbackCarriersMap[carrierId] = {
          id: carrierId,
          selected: true,
          manuallyCreated:
            existingCarrierManualState.get(carrierId)?.manuallyCreated ?? false,
          createdBy: existingCarrierManualState.get(carrierId)?.createdBy ?? "",
        };
      });
      setLookupRailCarriers(fallbackCarriersMap);
    } finally {
      setLoadingLookupRailCarriers(false);
    }
  };

  const handleLookupCarrierToggle = (carrier: string) => {
    setLookupRailCarriers((prev) => {
      const existing = prev[carrier];
      return {
        ...prev,
        [carrier]: {
          id: carrier,
          selected: !(existing?.selected ?? false),
          manuallyCreated: existing?.manuallyCreated ?? false,
          createdBy: existing?.createdBy ?? "",
        },
      };
    });
  };

  const selectedLookupCarrierIds = Object.values(lookupRailCarriers)
    .filter((carrier) => carrier.selected)
    .map((carrier) => carrier.id);

  const selectedLookupCarrierRecords = Object.values(lookupRailCarriers)
    .filter((carrier) => carrier.selected)
    .map(
      (carrier) =>
        ({
          id: carrier.id,
          manuallyCreated: carrier.manuallyCreated,
          createdBy: carrier.createdBy,
        } as ICarrier),
    );

  const lookupCarriersSelectedMap = Object.fromEntries(
    Object.entries(lookupRailCarriers).map(([carrierName, carrier]) => [
      carrierName,
      carrier.selected,
    ]),
  );
  const seededAdditionalStationRowIds = additionalStationRowIds.filter(
    (rowId) => rowId.startsWith("existing-"),
  );

  const otherRailStations = [
    ...(activeSearchRowId === null || !location.stationState
      ? []
      : [
          {
            scrsId: location.scrsId,
            stationState: location.stationState,
            carriers: location.carriers,
          },
        ]),
    ...additionalStationRowIds
      .filter(
        (rowId) => rowId !== activeSearchRowId && additionalStationData[rowId],
      )
      .map((rowId) => additionalStationData[rowId]),
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        padding: 3,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <div
          id="rail-station-carrier-label"
          style={{
            fontSize: 14,
            color: "#62748e",
            lineHeight: "14px",
            paddingBottom: "4px",
          }}
        >
          Stations &amp; Carriers*
        </div>
        <div
          id="rail-station-carrier-wrapper"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div
            id="rail-station-carrier-container"
            style={{
              display: "flex",
              gap: 0,
              height: 32,
              alignItems: "center",
              order: getRailStationRowOrder(
                MAIN_RAIL_STATION_ROW_ID,
                seededAdditionalStationRowIds,
                additionalStationRowIds,
              ),
            }}
          >
            <div
              id="rail-station-carrier-display"
              style={{
                flex: "1 0 0",
                minHeight: 25,
                background: "#f8fafc",
                border: "1.25px solid #e2e8f0",
                borderRadius: stationDisplay ? "3.5px 0 0 3.5px" : 3.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 6,
                padding: "4px 8px",
                fontSize: 14,
                color: stationDisplay ? "#0a0a0a" : "#878787",
                overflow: "hidden",
                height: "100%",
                boxSizing: "border-box",
              }}
            >
              <span
                id="rail-station-display-text"
                style={{
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {stationDisplay || (
                  <span style={{ fontStyle: "italic" }}>
                    No station selected
                  </span>
                )}
              </span>
            </div>
            <button
              id="rail-station-edit-button"
              data-testid="rail-station-edit-button"
              type="button"
              onClick={() => handleOpenSearch()}
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "0 8px",
                background: "#f8fafc",
                border: "1.25px solid #e2e8f0",
                borderRadius: "0 3.5px 3.5px 0",
                color: "#0a0a0a",
                fontFamily: "Inter",
                fontSize: 14,
                cursor: "pointer",
                flexShrink: 0,
                boxSizing: "border-box",
              }}
            >
              <EditIcon style={{ fontSize: 24, color: "#475569" }} />
              Edit
            </button>
            <IconButton
              id="rail-station-clear-button"
              data-testid="rail-station-clear-button"
              aria-label="Remove station and carriers"
              disabled={!stationDisplay}
              onClick={() => setPendingDeleteRowId("main")}
              size="small"
              style={{
                padding: 0,
                width: 34,
                height: 32,
                marginLeft: 8,
                flexShrink: 0,
              }}
            >
              <img
                src={TrashCanIcon}
                alt=""
                style={{ width: 20, height: 19 }}
              />
            </IconButton>
          </div>
          {additionalStationRowIds.map((rowId) => {
            const rowData = additionalStationData[rowId];
            const rowOrder = getRailStationRowOrder(
              rowId,
              seededAdditionalStationRowIds,
              additionalStationRowIds,
            );
            let rowDisplay = "";
            if (rowData) {
              rowDisplay = rowData.carriers.length
                ? `${rowData.stationState} (${rowData.carriers
                    .map((carrier) => carrier.id)
                    .join(", ")})`
                : rowData.stationState;
            }

            return (
              <div
                key={rowId}
                id={`rail-station-carrier-container-${rowId}`}
                data-testid="rail-station-carrier-container-additional"
                style={{
                  display: "flex",
                  gap: 0,
                  height: 32,
                  alignItems: "center",
                  order: rowOrder,
                }}
              >
                <div
                  id={`rail-station-carrier-display-${rowId}`}
                  style={{
                    flex: "1 0 0",
                    minHeight: 25,
                    background: "#f8fafc",
                    border: "1.25px solid #e2e8f0",
                    borderRadius: rowDisplay ? "3.5px 0 0 3.5px" : 3.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 6,
                    padding: "4px 8px",
                    fontSize: 14,
                    color: rowDisplay ? "#0a0a0a" : "#878787",
                    overflow: "hidden",
                    height: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {rowDisplay || (
                      <span style={{ fontStyle: "italic" }}>
                        No station selected
                      </span>
                    )}
                  </span>
                </div>
                <button
                  data-testid="rail-station-edit-button-additional"
                  type="button"
                  onClick={() => handleOpenSearch(rowId)}
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "0 8px",
                    background: "#f8fafc",
                    border: "1.25px solid #e2e8f0",
                    borderRadius: "0 3.5px 3.5px 0",
                    color: "#0a0a0a",
                    fontFamily: "Inter",
                    fontSize: 14,
                    cursor: "pointer",
                    flexShrink: 0,
                    boxSizing: "border-box",
                  }}
                >
                  <EditIcon style={{ fontSize: 24, color: "#475569" }} />
                  Edit
                </button>
                <IconButton
                  data-testid="rail-station-clear-button-additional"
                  aria-label="Remove station row"
                  onClick={() => {
                    if (rowData) {
                      setPendingDeleteRowId(rowId);
                      return;
                    }
                    setAdditionalStationRowIds((prev) =>
                      prev.filter((id) => id !== rowId),
                    );
                  }}
                  size="small"
                  style={{
                    padding: 0,
                    width: 34,
                    height: 32,
                    marginLeft: 8,
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={TrashCanIcon}
                    alt=""
                    style={{ width: 20, height: 19 }}
                  />
                </IconButton>
              </div>
            );
          })}
        </div>
        {stationDisplay && (
          <button
            id="rail-station-add-button"
            data-testid="rail-station-add-button"
            type="button"
            onClick={() =>
              handleOpenSearch(
                `new-${Date.now()}-${additionalStationRowIds.length}`,
              )
            }
            style={{
              alignSelf: "flex-start",
              margin: "16px 0",
              background: "none",
              border: "none",
              padding: 0,
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "14px",
              color: "#155dfc",
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            + Add Station &amp; Carrier
          </button>
        )}
      </div>

      {/* Equipment */}
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <div
          style={{
            fontSize: 14,
            color: "#62748e",
            lineHeight: "14px",
          }}
        >
          Equipment
        </div>
        <Select
          id="rail-equipment-type-select"
          size="small"
          value={location.equipmentTypes || []}
          multiple
          onChange={(e) => {
            setLocation({
              ...location,
              equipmentTypes: e.target.value as any,
            });
          }}
          renderValue={(selected) => selected.join(", ")}
          IconComponent={EquipmentDropdownIcon}
          style={{
            background: "#f8fafc",
            border: "1.25px solid #e2e8f0",
            borderRadius: 3.5,
            height: 32,
            fontSize: 14,
          }}
          MenuProps={{
            ...(isMobile
              ? {
                  sx: {
                    zIndex: 13050,
                  },
                  disableScrollLock: true,
                }
              : {}),
            PaperProps: {
              style: {
                maxHeight: 140,
                ...(isMobile ? { zIndex: 13051 } : {}),
              },
            },
          }}
        >
          <MenuItem value="SINGLE">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Checkbox
                sx={{ padding: 0, margin: 0 }}
                checked={location.equipmentTypes?.includes("SINGLE") || false}
              />
              <div>SINGLE</div>
            </div>
          </MenuItem>
          <MenuItem value="UNIT TRAIN">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Checkbox
                sx={{ padding: 0, margin: 0 }}
                checked={
                  location.equipmentTypes?.includes("UNIT TRAIN") || false
                }
              />
              <div>UNIT TRAIN</div>
            </div>
          </MenuItem>
        </Select>
      </div>

      {pendingDeleteRowId && (
        <DeleteRailStationConfirmation
          confirm={() => {
            if (pendingDeleteRowId === "main") {
              const promoteRowId = additionalStationRowIds.find(
                (rowId) => additionalStationData[rowId],
              );
              const promoted = promoteRowId
                ? additionalStationData[promoteRowId]
                : undefined;
              setLocation({
                ...location,
                stationState: promoted?.stationState,
                scrsId: promoted?.scrsId,
                carriers: promoted?.carriers || [],
              });
              if (promoteRowId) {
                setAdditionalStationData((prev) => {
                  const next = { ...prev };
                  delete next[promoteRowId];
                  return next;
                });
                setAdditionalStationRowIds((prev) =>
                  prev.filter((id) => id !== promoteRowId),
                );
              }
            } else {
              setAdditionalStationData((prev) => {
                const next = { ...prev };
                delete next[pendingDeleteRowId];
                return next;
              });
              setAdditionalStationRowIds((prev) =>
                prev.filter((id) => id !== pendingDeleteRowId),
              );
            }
            setPendingDeleteRowId(null);
          }}
          cancel={() => setPendingDeleteRowId(null)}
        />
      )}

      {isMobile && (
        <>
          <RailStationLookupPanel
            open={showRailStationLookupPanel}
            destinationName={location.name || location.zipCode || "Destination"}
            defaultFilterLabel={`${location.city || ""}${
              location.state ? `, ${location.state}` : ""
            }`}
            selectedScrsId={isMobile ? location.scrsId || "" : ""}
            stations={[]}
            loading={false}
            onClose={() => setShowRailStationLookupPanel(false)}
            onNext={handleLookupRailStationNext}
            onDisplayStationsEmptyChange={() => {}}
          />

          <RailStationCarrierReviewPanel
            open={showRailCarrierReviewPanel}
            destinationName={location.name || location.zipCode || "Destination"}
            stationName={selectedLookupStation}
            carriers={lookupCarriersSelectedMap}
            loading={loadingLookupRailCarriers}
            locationId={location.id}
            selectedScrsId={selectedLookupScrsId || undefined}
            existingModes={
              toModes(location.inboundModes, location.outboundModes) as any
            }
            userAddedCarriers={lookupUserAddedCarriers}
            marketerName={`${auth.user?.profile?.given_name || ""} ${
              auth.user?.profile?.family_name || ""
            }`.trim()}
            isMobileFinishFlow={isMobile}
            multiRailStation={featureFlags.multiRailStation}
            deferLocationSave
            existingRailStations={otherRailStations}
            onBack={() => {
              setShowRailCarrierReviewPanel(false);
              setShowRailStationLookupPanel(true);
            }}
            onClose={() => setShowRailCarrierReviewPanel(false)}
            onToggleCarrier={handleLookupCarrierToggle}
            onAddCarrier={() => {
              setShowRailCarrierReviewPanel(false);
              setShowRailAddCarrierPanel(true);
            }}
            onNext={() => {
              setLocation({
                ...location,
                stationState: selectedLookupStation,
                carriers: selectedLookupCarrierRecords,
              });
              setShowRailCarrierReviewPanel(false);
            }}
            onFinishSuccess={({ railStation, carriers, scrsId }) => {
              const updatedCarriers = carriers.map((carrierId) => ({
                id: carrierId,
                manuallyCreated:
                  lookupRailCarriers[carrierId]?.manuallyCreated ?? false,
                createdBy: lookupRailCarriers[carrierId]?.createdBy ?? "",
              }));

              if (activeSearchRowId) {
                setAdditionalStationData((prev) => ({
                  ...prev,
                  [activeSearchRowId]: {
                    scrsId,
                    stationState: railStation,
                    carriers: updatedCarriers,
                  },
                }));
                setAdditionalStationRowIds((prev) =>
                  prev.includes(activeSearchRowId)
                    ? prev
                    : [...prev, activeSearchRowId],
                );
              } else {
                setLocation({
                  ...location,
                  stationState: railStation,
                  carriers: updatedCarriers,
                  scrsId,
                });
              }
              setActiveSearchRowId(null);
              setShowRailCarrierReviewPanel(false);
            }}
            onFinishError={() => {
              setShowRailCarrierReviewPanel(true);
            }}
          />

          <RailStationAddCarrierPanel
            open={showRailAddCarrierPanel}
            initialSelectedCarriers={selectedLookupCarrierIds}
            onBack={(selectedCarriers) => {
              const existingCarrierNames = Object.keys(lookupRailCarriers);
              const newlyAdded = selectedCarriers.filter(
                (carrier) => !existingCarrierNames.includes(carrier),
              );
              if (newlyAdded.length > 0) {
                setLookupUserAddedCarriers((prev) =>
                  Array.from(new Set([...prev, ...newlyAdded])),
                );
              }
              setLookupRailCarriers((prev) => {
                const next = { ...prev };
                selectedCarriers.forEach((carrier) => {
                  const existing = next[carrier];
                  next[carrier] = {
                    id: carrier,
                    selected: true,
                    manuallyCreated: existing?.manuallyCreated ?? true,
                    createdBy: existing?.createdBy ?? "",
                  };
                });
                return next;
              });
              setShowRailAddCarrierPanel(false);
              setShowRailCarrierReviewPanel(true);
            }}
            onApplySelected={async (selectedCarriers) => {
              const existingCarrierNames = Object.keys(lookupRailCarriers);
              const newlyAdded = selectedCarriers.filter(
                (carrier) => !existingCarrierNames.includes(carrier),
              );

              if (selectedCarriers.length > 0 && selectedLookupStation) {
                await NitroService.createCarrierForStation({
                  stationState: selectedLookupStation,
                  carriers: selectedCarriers,
                  username: `${auth.user?.profile?.given_name || ""} ${
                    auth.user?.profile?.family_name || ""
                  }`.trim(),
                });
              }

              if (newlyAdded.length > 0) {
                setLookupUserAddedCarriers((prev) =>
                  Array.from(new Set([...prev, ...newlyAdded])),
                );
              }
              setLookupRailCarriers((prev) => {
                const next = { ...prev };
                selectedCarriers.forEach((carrier) => {
                  const existing = next[carrier];
                  next[carrier] = {
                    id: carrier,
                    selected: true,
                    manuallyCreated: existing?.manuallyCreated ?? true,
                    createdBy: existing?.createdBy ?? "",
                  };
                });
                return next;
              });
              setShowRailAddCarrierPanel(false);
              setShowRailCarrierReviewPanel(true);
            }}
            onClose={() => {
              setShowRailAddCarrierPanel(false);
              setShowRailCarrierReviewPanel(true);
            }}
          />
        </>
      )}
    </div>
  );
};

export default RailSettings;
